import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerMailDialog } from "@/components/CustomerMailDialog";
import { useAuth } from "@/context/AuthContext";
import { Mail, FileCheck2, FileDown, UploadCloud } from "lucide-react";
import { DuReminderSettings } from "@/components/DuReminderSettings";
import { WORKFLOW, downloadBlob, useVat, eur, vatHint } from "@/lib/workflow";
import { DeclineDialog } from "@/components/DeclineDialog";
import { PublicBookingLink } from "@/components/PublicBookingLink";
import { ConfirmPriceDialog } from "@/components/ConfirmPriceDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "");

const priceOf = (r) => Number(r.final_price ?? r.booking?.price ?? 0);

function Revenue({ rows }) {
  const vat = useVat();
  const sum = (s) => rows.filter((r) => r.du_status === s).reduce((a, r) => a + priceOf(r), 0);
  const n = (s) => rows.filter((r) => r.du_status === s).length;
  const cards = [
    ["Zugesagt", sum("zugesagt"), `${n("zugesagt")} Anzeigen · ${vatHint(vat)}`, "border-emerald-500", "rev-confirmed"],
    ["In Prüfung", sum("eingegangen"), `${n("eingegangen")} Buchungen eingegangen · ${vatHint(vat)}`, "border-amber-500", "rev-pending"],
    ["Erwartet gesamt", sum("zugesagt") + sum("eingegangen"), `${vatHint(vat)} · brutto ${eur((sum("zugesagt") + sum("eingegangen")) * (1 + vat / 100))}`, "border-slate-900", "rev-total"],
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-3 mb-5" data-testid="revenue-summary">
      {cards.map(([l, v, sub, b, id]) => (
        <div key={l} className={`bg-white border border-slate-200 border-l-4 ${b} rounded-xl px-4 py-3 shadow-sm`} data-testid={id}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{eur(v)}</div>
          <div className="text-xs text-slate-500">{sub}</div>
        </div>
      ))}
    </div>
  );
}

export function StatusButtons({ statuses, current, onSet, disabled, testid }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {statuses.map(([k, l, on]) => (
        <button key={k} type="button" disabled={disabled} onClick={() => current !== k && onSet(k)} data-testid={`${testid}-${k}`}
          className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${current === k ? on : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"} disabled:opacity-60`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function MailInfo({ mails }) {
  if (!mails.length) return <span className="text-slate-400">–</span>;
  const [first, ...more] = mails;
  return (
    <div className="text-xs">
      <div title={first.subject}><span className="text-slate-500">Erstanfrage:</span> <b>{fmt(first.at)}</b></div>
      {more.length > 0 && <div className="text-slate-500" title={more.map((m) => `${fmt(m.at)}: ${m.subject}`).join("\n")}>+ {more.length} weitere, zuletzt {fmt(more[more.length - 1].at)}</div>}
    </div>
  );
}

function Row({ r, cfg, canMail, canEdit, checked, onToggle, onOpen, onSet, onMail }) {
  const noAd = r.type === "partner" && !r.ad_format_id;
  return (
    <tr onClick={onOpen} className={`cursor-pointer hover:bg-slate-50 ${noAd ? "opacity-60" : ""}`} data-testid={`workflow-row-${r.id}`}>
      {canMail && <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={checked} disabled={!r.email || noAd} title={noAd ? "Keine Freianzeige – kein Mailversand" : undefined} onChange={onToggle} className="h-4 w-4 accent-blue-600 disabled:opacity-30" data-testid={`workflow-select-${r.id}`} /></td>}
      <td className="px-4 py-3">
        <Link to={`${cfg.detailPath}/${r.id}`} onClick={(e) => e.stopPropagation()} className="font-semibold text-slate-900 hover:underline">{r.name}</Link>
        {r.uploaded_at && <span title={`Über Upload-Link hochgeladen am ${new Date(r.uploaded_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}${r.upload_filename ? ` – ${r.upload_filename}` : ""}`} className="ml-1.5 inline-flex align-middle text-emerald-600" data-testid={`workflow-uploaded-${r.id}`}><UploadCloud size={15} /></span>}
        <div className="text-xs text-slate-500">{r.contact}</div>
        {noAd && <div className="text-xs text-amber-700" data-testid={`workflow-no-free-ad-${r.id}`}>keine Freianzeige mehr – kein Mailversand</div>}
      </td>
      <td className="px-4 py-3 text-slate-600">{r.booking ? <><div>{r.booking.format}</div><div className="text-xs text-slate-400">{r.booking.price} € netto</div>{r.booking.price_note && <div className="text-xs text-amber-700 mt-0.5" title={r.booking.price_note} data-testid={`workflow-price-note-${r.id}`}>Anmerkung: {r.booking.price_note}</div>}{r.final_price != null && <div className="text-xs text-emerald-700 font-semibold mt-0.5" data-testid={`workflow-final-price-${r.id}`}>Endpreis: {r.final_price} € netto</div>}{r.order_no && <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`workflow-order-no-${r.id}`}>Auftrag {r.order_no}</div>}</> : <>{r.ad_format || "–"}{r.amount ? <div className="text-xs text-slate-400">{r.amount} €</div> : null}</>}</td>
      <td className="px-4 py-3"><MailInfo mails={r.mails} /></td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <StatusButtons statuses={cfg.statuses} current={r.du_status} onSet={(s) => onSet(r, s)} disabled={!canEdit} testid={`du-${r.id}`} />
        {r.article_id && <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1"><FileCheck2 size={12} /> Artikel in „Anzeigen“ angelegt</div>}
      </td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" disabled={!r.email || !canMail || noAd} onClick={onMail} title="E-Mail senden" data-testid={`workflow-mail-${r.id}`}><Mail size={16} className="text-blue-600" /></Button>
      </td>
    </tr>
  );
}

export default function AdWorkflow({ type = "partner" }) {
  const cfg = WORKFLOW[type];
  const [data, setData] = useState(null);
  const [issueId, setIssueId] = useState("");
  const [filter, setFilter] = useState(cfg.defaultFilter || "alle");
  const [decline, setDecline] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [sel, setSel] = useState([]);
  const [mailFor, setMailFor] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.permissions?.[cfg.area] === "edit";
  const canMail = !!user?.is_chef || canEdit;

  const load = useCallback(() => {
    api.get("/customers-workflow", { params: { type, ...(issueId ? { issue_id: issueId } : {}) } })
      .then((r) => { setData(r.data); if (!issueId && r.data.issue) setIssueId(r.data.issue.id); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [issueId, type]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("mailjobs-done", load);
    return () => window.removeEventListener("mailjobs-done", load);
  }, [load]);

  const setDu = async (row, status, extra = {}) => {
    if (status === "abgesagt" && type === "bezahlkunde" && !extra.reason && extra.send_mail === undefined) { setDecline(row); return; }
    if (status === "zugesagt" && type === "bezahlkunde" && extra.final_price === undefined) { setConfirmRow(row); return; }
    try {
      const { data: r } = await api.put(`/customers-workflow/${data.issue.id}/${row.id}`, { du_status: status, ...extra });
      toast.success(status === cfg.articleOn ? `Artikel „Anzeige – ${row.name}“ angelegt` : r.article_id === null && row.article_id ? "Status geändert – Anzeigen-Artikel entfernt" : "Status geändert");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 text-slate-400">Lädt…</div>;
  const rows = data.rows.filter((r) => filter === "alle" || r.du_status === filter || (cfg.filters || []).some(([k, , s]) => k === filter && s.includes(r.du_status)));
  const counts = Object.fromEntries(cfg.statuses.map(([k]) => [k, data.rows.filter((r) => r.du_status === k).length]));
  const eligible = rows.filter((r) => r.email && !(r.type === "partner" && !r.ad_format_id));
  const allOn = eligible.length > 0 && eligible.every((r) => sel.includes(r.id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="p-6 lg:p-8" data-testid="ad-workflow-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{cfg.title}</h1>
          <p className="text-slate-500 mt-1">{cfg.sub}</p>
        </div>
        <div className="w-72">
          <Button variant="outline" size="sm" onClick={() => downloadBlob("/customers-workflow-pdf", { issue_id: data.issue.id, type }, `${cfg.pdfName}_${data.issue.name}.pdf`)} disabled={!data.issue} className="w-full mb-2" data-testid="workflow-pdf-button"><FileDown size={15} className="mr-1.5" /> Auswertung als PDF</Button>
          <Select value={issueId} onValueChange={(v) => { setIssueId(v); setSel([]); }}>
            <SelectTrigger data-testid="workflow-issue-select"><SelectValue placeholder="Ausgabe wählen" /></SelectTrigger>
            <SelectContent>{data.issues.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.status === "archiv" ? " (Archiv)" : ""}</SelectItem>)}</SelectContent>
          </Select>
          {data.issue?.redaktionsschluss && <div className="text-xs text-slate-500 mt-1" data-testid="workflow-deadline">Redaktionsschluss: <b>{fmt(data.issue.redaktionsschluss)}</b></div>}
        </div>
      </div>

      {type === "partner" && user?.role === "admin" && <DuReminderSettings />}
      {type === "bezahlkunde" && <PublicBookingLink />}
      {type === "bezahlkunde" && <Revenue rows={data.rows} />}

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg flex-wrap">
          {[...(cfg.filters || []).map(([k, l, s]) => [k, `${l} (${data.rows.filter((r) => s.includes(r.du_status)).length})`]), ["alle", `Alle (${data.rows.length})`], ...cfg.statuses.map(([k, l]) => [k, `${l} (${counts[k]})`])].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`workflow-filter-${k}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>
        {canMail && sel.length > 0 && (
          <Button size="sm" onClick={() => setMailFor(data.rows.filter((r) => sel.includes(r.id)))} className="bg-blue-600 hover:bg-blue-700" data-testid="workflow-bulk-mail">
            <Mail size={14} className="mr-1.5" /> E-Mail an {sel.length} Empfänger
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="workflow-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              {canMail && <th className="pl-4 py-3 w-8"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : eligible.map((r) => r.id))} className="h-4 w-4 accent-blue-600" data-testid="workflow-select-all" /></th>}
              <th className="text-left px-4 py-3">{cfg.one}</th>
              <th className="text-left px-4 py-3">{cfg.adLabel}</th>
              <th className="text-left px-4 py-3">Mails zu dieser Ausgabe</th>
              <th className="text-left px-4 py-3">{cfg.statusLabel}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <Row key={r.id} r={r} cfg={cfg} canMail={canMail} canEdit={canEdit} checked={sel.includes(r.id)} onToggle={() => toggle(r.id)}
                onOpen={() => navigate(`${cfg.path}/${r.id}?issue=${data.issue.id}`, { state: { ids: rows.map((x) => x.id) } })}
                onSet={setDu} onMail={() => setMailFor([r])} />
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{cfg.empty}</td></tr>}
          </tbody>
        </table>
      </div>
      <ConfirmPriceDialog row={confirmRow} onCancel={() => setConfirmRow(null)} onConfirm={(x) => { const r = confirmRow; setConfirmRow(null); setDu(r, "zugesagt", x); }} />
      <DeclineDialog customer={decline} onCancel={() => setDecline(null)} onConfirm={(x) => { const r = decline; setDecline(null); setDu(r, "abgesagt", x); }} />
      <CustomerMailDialog customers={mailFor} onClose={(done) => { if (done) { setSel([]); load(); } setMailFor(null); }} />
    </div>
  );
}
